import { useMemo } from 'react'
import { useLink } from '@ggs/hooks'
import { LoadingButton as MUILoadButton } from '@mui/lab'
import { noop } from 'lodash'
import './Button.scss'

const ButtonVariant = {
  contained: /** @type {'contained'} */ ('contained'),
  outlined: /** @type {'outlined'} */ ('outlined'),
  text: /** @type {'text'} */ ('text'),
}

const ButtonColor = {
  primary: /** @type {'primary'} */ ('primary'),
  secondary: /** @type {'secondary'} */ ('secondary'),
  success: /** @type {'success'} */ ('success'),
  error: /** @type {'error'} */ ('error'),
  info: /** @type {'info'} */ ('info'),
  warning: /** @type {'warning'} */ ('warning'),
  inherit: /** @type {'inherit'} */ ('inherit'),
}

const ButtonSize = {
  small: /** @type {'small'} */ ('small'),
  medium: /** @type {'medium'} */ ('medium'),
  large: /** @type {'large'} */ ('large'),
}
/**
 * @typedef {object} ButtonProps
 * @property {string=} label
 * @property {string=} loadingLabel
 * @property {string=} type
 * @property {'small'|'medium'|'large'=} size
 * @property {'text'|'outlined'|'contained'=} variant
 * @property {'primary'|'secondary'|'error'|'info'|'success'|'warning'|'inherit'=} color
 * @property {string=} link
 * @property {function=} onClick
 * @property {boolean=} isLoading
 * @property {JSX.Element|string=} children
 * @property {Object<string, string>=} classes
 * @property {Object<string, any>=} buttonFields
 */

/**
 * @param {ButtonProps} props
 * @return {JSX.Element}
 */
const Button = ({
  label = '',
  loadingLabel = 'Loading',
  type = 'button',
  size = ButtonSize.large,
  variant = ButtonVariant.contained,
  color = ButtonColor.secondary,
  link = '',
  onClick = noop,
  isLoading = false,
  children = null,
  classes = {},
  buttonFields = {},
}) => {
  const safeUrl = useLink()
  const {
    isExternal,
    uri,
    label: linkLabel,
    isAssetLink,
  } = useMemo(() => safeUrl({ link }), [link])

  const action = useMemo(
    () =>
      uri
        ? () =>
            isExternal || isAssetLink ? window.open(uri, '_blank', 'noopener, noreferrer') : uri
        : onClick,
    [uri, onClick]
  )

  return (
    <MUILoadButton
      {...{ type, size, variant, color }}
      disableElevation
      loading={isLoading}
      classes={{
        root: `btn btn--root ${classes.root ?? ''}`.trim(),
        contained: `btn--contained ${classes.contained ?? ''}`.trim(),
        text: `btn--text ${classes.text ?? ''}`.trim(),
      }}
      onClick={() => action()}
      href={uri}
      {...buttonFields}
    >
      <span className="btn__label">
        {isLoading ? loadingLabel : linkLabel || label || children}
      </span>
    </MUILoadButton>
  )
}

export default Button

Button.color = ButtonColor
Button.size = ButtonSize
Button.variant = ButtonVariant
